/**
 * TaskList — shared `.tl` grid view used by both the global Tasks page
 * (`/tasks`) and the project-scoped list (`/projects/<id>/list`).
 *
 * Markup mirrors `designs/TaskList.html`:
 *
 *   .tl > .tl-head + (.tl-group-row + .tl-row*)*
 *
 * 7-column grid:
 *   [checkbox] [Task w/ ID] [Status] [Priority] [Assignee] [Due] [Project]
 *
 * When `showProjectColumn={false}` (project-scoped view), the project
 * column is omitted from both header and rows. Status grouping renders
 * collapsible group-rows; sortable column headers toggle asc/desc.
 *
 * Data dependencies live with the caller; the two routes diverge only in
 * how they source `tasks`, `userNames`, and `projects`.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { Avatar } from "./Avatar";
import { Icon } from "./Icon";
import { Pill } from "./Pill";
import { PriorityChip } from "./PriorityChip";
import { classifyDue, formatDateShort, parseDateLocal } from "@/lib/format";
import { TASK_STATUS_LABEL, taskStatusOrder } from "@/lib/taskStatus";
import type { TaskRow, TaskStatus } from "@shared/types";

type StatusKey = TaskStatus;

const STATUS_DOT: Record<StatusKey, string> = {
  backlog: "var(--muted)",
  todo: "var(--blue-500)",
  doing: "var(--orange-500)",
  done: "var(--green-500)",
};
const STATUS_ORDER: StatusKey[] = taskStatusOrder;
const STATUS_RANK: Record<StatusKey, number> = {
  backlog: 0,
  todo: 1,
  doing: 2,
  done: 3,
};

type SortKey = "title" | "status" | "priority" | "assignee" | "due" | "project";

interface TaskLike {
  id: string;
  title: string;
  status: TaskRow["status"];
  priority: number;
  assigneeId: string | null;
  assigneeIds?: string[];
  dueDate: string | null;
  projectId: string;
}

export interface TaskListProps {
  tasks: TaskLike[];
  /** Map of userId → display name, for the assignee column */
  userNames?: ReadonlyMap<string, string>;
  /** Map of userId → uploaded photo URL, for the assignee avatar */
  userImages?: ReadonlyMap<string, string | null>;
  /** Map of projectId → {name, color}; used by the Project column */
  projects?: ReadonlyMap<string, { name: string; color: string | null }>;
  /** Hide the project column on project-scoped lists. Default: true */
  showProjectColumn?: boolean;
  /** Optional callback fired when one or more rows are checkbox-selected */
  onSelectionChange?: (ids: string[]) => void;
  /** Increment to clear the selection from outside — e.g. after a bulk
   * action completes. Without this the checkboxes stayed visually checked
   * while the caller's copy of the selection was already reset. */
  selectionResetKey?: number;
  /** Group rows by status with collapsible group headers. Default: true */
  groupByStatus?: boolean;
  /** Initial search filter text (rendered controlled by caller when needed) */
}

function dueClassify(due: string | null): "" | "soon" | "over" {
  // The .tl-due CSS uses `.over` / `.soon` — map "overdue" → "over"
  // to preserve the existing class names.
  const c = classifyDue(due);
  return c === "overdue" ? "over" : c;
}

function formatDue(due: string | null): string {
  return formatDateShort(due) || "—";
}

function capStatus(s: string): string {
  return s.length > 0 ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

function isStatusKey(s: string): s is StatusKey {
  return s === "backlog" || s === "todo" || s === "doing" || s === "done";
}

export function TaskList({
  tasks,
  userNames,
  userImages,
  projects,
  showProjectColumn = true,
  onSelectionChange,
  selectionResetKey,
  groupByStatus = true,
}: TaskListProps) {
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<1 | -1>(1);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [collapsed, setCollapsed] = useState<Set<StatusKey>>(new Set());
  const navigate = useNavigate();

  const lastResetKey = useRef(selectionResetKey);
  useEffect(() => {
    if (selectionResetKey !== lastResetKey.current) {
      lastResetKey.current = selectionResetKey;
      setSelected(new Set());
    }
  }, [selectionResetKey]);

  const sorted = useMemo(() => {
    if (!sortKey) return tasks;
    const arr = [...tasks];
    arr.sort((a, b) => {
      let av: string | number = "";
      let bv: string | number = "";
      switch (sortKey) {
        case "title":
          av = a.title.toLowerCase();
          bv = b.title.toLowerCase();
          break;
        case "status":
          av = isStatusKey(a.status) ? STATUS_RANK[a.status] : 99;
          bv = isStatusKey(b.status) ? STATUS_RANK[b.status] : 99;
          break;
        case "priority":
          av = a.priority;
          bv = b.priority;
          break;
        case "assignee": {
          const aid = a.assigneeId ?? a.assigneeIds?.[0] ?? "";
          const bid = b.assigneeId ?? b.assigneeIds?.[0] ?? "";
          av = (userNames?.get(aid) ?? aid).toLowerCase();
          bv = (userNames?.get(bid) ?? bid).toLowerCase();
          break;
        }
        case "due": {
          const ad = parseDateLocal(a.dueDate);
          const bd = parseDateLocal(b.dueDate);
          av = ad ? ad.getTime() : Number.MAX_SAFE_INTEGER;
          bv = bd ? bd.getTime() : Number.MAX_SAFE_INTEGER;
          break;
        }
        case "project":
          av = (projects?.get(a.projectId)?.name ?? a.projectId).toLowerCase();
          bv = (projects?.get(b.projectId)?.name ?? b.projectId).toLowerCase();
          break;
      }
      if (av < bv) return -1 * sortDir;
      if (av > bv) return 1 * sortDir;
      return 0;
    });
    return arr;
  }, [tasks, sortKey, sortDir, userNames, projects]);

  const groups = useMemo(() => {
    if (!groupByStatus)
      return [{ key: null as StatusKey | null, items: sorted }];
    const buckets = new Map<StatusKey, TaskLike[]>();
    for (const k of STATUS_ORDER) buckets.set(k, []);
    for (const t of sorted) {
      const k = isStatusKey(t.status) ? t.status : "backlog";
      buckets.get(k)!.push(t);
    }
    return STATUS_ORDER.map((k) => ({ key: k, items: buckets.get(k)! })).filter(
      (g) => g.items.length > 0,
    );
  }, [sorted, groupByStatus]);

  const allIds = sorted.map((t) => t.id);
  const allSelected =
    allIds.length > 0 && allIds.every((id) => selected.has(id));

  const emitSelection = (next: Set<string>) => {
    setSelected(next);
    onSelectionChange?.(Array.from(next));
  };

  const toggleAll = (on: boolean) => {
    emitSelection(on ? new Set(allIds) : new Set());
  };

  const toggleRow = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    emitSelection(next);
  };

  const toggleGroup = (k: StatusKey) => {
    setCollapsed((s) => {
      const next = new Set(s);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };

  const onSort = (k: SortKey) => {
    if (sortKey === k) setSortDir((d) => (d === 1 ? -1 : 1));
    else {
      setSortKey(k);
      setSortDir(1);
    }
  };

  // Column widths mirror the design grid; gate the project column.
  const gridStyle = {
    gridTemplateColumns: showProjectColumn
      ? "36px minmax(240px,2.6fr) 120px 110px 158px 104px 168px"
      : "36px minmax(240px,2.6fr) 120px 110px 158px 104px",
  } as const;

  const renderTH = (key: SortKey, label: string) => (
    <div
      className={`th${sortKey === key ? " sorted" : ""}`}
      onClick={() => onSort(key)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onSort(key);
      }}
    >
      {label}
      <span className="sort">
        <Icon name={sortDir === 1 ? "arrow-d" : "arrow-u"} size={12} />
      </span>
    </div>
  );

  return (
    <div className="tl">
      {/* Header row */}
      <div className="tl-head" style={gridStyle}>
        <div>
          <label
            className={`cb${allSelected ? " on" : ""}`}
            aria-label="Select all tasks"
          >
            <input
              type="checkbox"
              checked={allSelected}
              onChange={(e) => toggleAll(e.target.checked)}
              style={{
                position: "absolute",
                opacity: 0,
                width: 0,
                height: 0,
              }}
            />
            {allSelected ? <Icon name="check" size={12} /> : null}
          </label>
        </div>
        {renderTH("title", "Task")}
        {renderTH("status", "Status")}
        {renderTH("priority", "Priority")}
        {renderTH("assignee", "Assignee")}
        {renderTH("due", "Due")}
        {showProjectColumn ? renderTH("project", "Project") : null}
      </div>

      {/* Groups + rows */}
      {groups.map((g) => {
        const isCol = g.key !== null && collapsed.has(g.key);
        return (
          <div key={g.key ?? "all"}>
            {g.key !== null && groupByStatus && (
              <div
                className={`tl-group-row${isCol ? " collapsed" : ""}`}
                onClick={() => toggleGroup(g.key as StatusKey)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ")
                    toggleGroup(g.key as StatusKey);
                }}
              >
                <span className="gchev">
                  <Icon name="chev" size={13} />
                </span>
                <span
                  className="gdot"
                  style={{ background: STATUS_DOT[g.key] }}
                />
                <span className="gname">{TASK_STATUS_LABEL[g.key]}</span>
                <span className="gcount">{g.items.length}</span>
              </div>
            )}
            {g.items.map((t) => {
              const isSel = selected.has(t.id);
              const dc = dueClassify(t.dueDate);
              const assigneeId = t.assigneeId ?? t.assigneeIds?.[0] ?? null;
              const assigneeName = assigneeId
                ? (userNames?.get(assigneeId) ?? assigneeId.slice(0, 8))
                : null;
              const proj = projects?.get(t.projectId);
              const openTask = () => {
                navigate({ to: "/tasks/$taskId", params: { taskId: t.id } });
              };
              return (
                <div
                  key={t.id}
                  role="link"
                  tabIndex={0}
                  aria-label={`Open task ${t.title}`}
                  className={`tl-row${isSel ? " sel" : ""}${isCol ? " collapsed" : ""}`}
                  style={{
                    ...gridStyle,
                    color: "inherit",
                    textDecoration: "none",
                  }}
                  data-id={t.id}
                  onClick={openTask}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      openTask();
                    }
                  }}
                >
                  <div
                    className="cell-cb"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      toggleRow(t.id);
                    }}
                  >
                    <span
                      className={`cb${isSel ? " on" : ""}`}
                      role="checkbox"
                      aria-checked={isSel}
                      aria-label={`Select ${t.title}`}
                    >
                      {isSel ? <Icon name="check" size={12} /> : null}
                    </span>
                  </div>
                  <div className="tl-task">
                    <span className="tid">#{t.id.slice(0, 6)}</span>
                    <span className="ttitle">{t.title}</span>
                  </div>
                  <div>
                    <Pill
                      variant={isStatusKey(t.status) ? t.status : "backlog"}
                      dot
                    >
                      {capStatus(t.status)}
                    </Pill>
                  </div>
                  <div>
                    <PriorityChip level={t.priority} />
                  </div>
                  <div className="tl-assignee">
                    {assigneeId ? (
                      <Link
                        to="/team/$userId"
                        params={{ userId: assigneeId }}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 8,
                          color: "inherit",
                          textDecoration: "none",
                        }}
                      >
                        <Avatar
                          id={assigneeId}
                          name={assigneeName ?? assigneeId}
                          size="xs"
                          image={userImages?.get(assigneeId)}
                        />
                        <span>{assigneeName}</span>
                      </Link>
                    ) : (
                      <span className="t-muted">—</span>
                    )}
                  </div>
                  <div className={`tl-due${dc ? ` ${dc}` : ""}`}>
                    {formatDue(t.dueDate)}
                  </div>
                  {showProjectColumn && (
                    <div className="tl-proj">
                      <Link
                        to="/projects/$projectId"
                        params={{ projectId: t.projectId }}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 8,
                          color: "inherit",
                          textDecoration: "none",
                        }}
                      >
                        <span
                          className="proj-dot"
                          style={{
                            width: 8,
                            height: 8,
                            background: proj?.color ?? "var(--muted)",
                          }}
                        />
                        <span className="ttitle">
                          {proj?.name ?? t.projectId.slice(0, 8)}
                        </span>
                      </Link>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
